import * as React from "react";
import { Link } from "react-router";
import { useCustom } from "@refinedev/core";
import { SkeletonTable } from "@/components/SkeletonTable";
import { ErrorBanner } from "@/components/ErrorBanner";
import { EmptyState } from "@/components/EmptyState";
import {
  UsageDailyChart,
  UsageKindBreakdown,
  UsagePeriodBar,
  UsageTotalsCards,
} from "@/components/UsageSummary";
import {
  formatCost,
  formatCount,
  formatDateTime,
  lastDaysPeriod,
  usagePeriodQuery,
} from "@/lib/usage";
import type { UsageOverview, UsagePeriodState } from "@/lib/usage";

const PAGE_SIZE = 20;

export function UsageList(): React.JSX.Element {
  const [period, setPeriod] = React.useState<UsagePeriodState>(() => lastDaysPeriod(30));
  const [page, setPage] = React.useState(0);

  // A new window restarts pagination — page 3 of the old window is meaningless.
  React.useEffect(() => {
    setPage(0);
  }, [period.from, period.to]);

  const params = new URLSearchParams(usagePeriodQuery(period).replace(/^\?/, ""));
  params.set("limit", String(PAGE_SIZE));
  params.set("offset", String(page * PAGE_SIZE));

  const { query } = useCustom<UsageOverview>({
    url: `/admin/usage?${params.toString()}`,
    method: "get",
    queryOptions: { queryKey: ["admin-usage", period.from, period.to, page] },
  });

  const overview = query.data?.data;

  return (
    <div className="p-6 md:p-8">
      <div className="flex items-baseline justify-between gap-4 mb-5">
        <h2 className="text-[22px] font-[700] tracking-tight">Расходы</h2>
        <p className="text-[13px] text-[var(--muted)]">
          Стоимость AI- и импорт-операций по данным usage_record
        </p>
      </div>

      <UsagePeriodBar
        value={period}
        onChange={setPeriod}
        trailing={
          <button
            type="button"
            onClick={() => void query.refetch()}
            className="h-9 px-3 rounded-[var(--radius-sm)] text-[13px] font-[500] border border-[var(--border)] text-[var(--muted)] bg-[var(--surface-2)] hover:text-[var(--fg)]"
          >
            Обновить
          </button>
        }
      />

      {query.isError && (
        <div className="mb-5">
          <ErrorBanner
            message={
              (query.error as { message?: string } | undefined)?.message ??
              "Не удалось загрузить расходы"
            }
            onRetry={() => void query.refetch()}
          />
        </div>
      )}

      {query.isLoading && !overview ? (
        <SkeletonTable rows={6} columns={5} />
      ) : overview ? (
        <>
          <UsageTotalsCards
            period={overview.period}
            allTime={overview.allTime}
            periodState={period}
            showAccounts
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
            <UsageKindBreakdown items={overview.byKind} />
            <UsageDailyChart points={overview.daily} />
          </div>

          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius)] overflow-hidden">
            <div className="px-5 py-3.5 border-b border-[var(--border)]">
              <h3 className="text-[14px] font-[600]">Аккаунты по расходам за период</h3>
            </div>

            {overview.items.length === 0 ? (
              <EmptyState message="За выбранный период расходов не было" />
            ) : (
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-[var(--border)]">
                    <th scope="col" className="px-3.5 py-2.5 text-left text-[11px] font-[600] uppercase tracking-wide text-[var(--muted)]">
                      Аккаунт
                    </th>
                    <th scope="col" className="px-3.5 py-2.5 text-right text-[11px] font-[600] uppercase tracking-wide text-[var(--muted)] w-[140px]">
                      Расходы
                    </th>
                    <th scope="col" className="px-3.5 py-2.5 text-right text-[11px] font-[600] uppercase tracking-wide text-[var(--muted)] w-[120px]">
                      Операции
                    </th>
                    <th scope="col" className="px-3.5 py-2.5 text-right text-[11px] font-[600] uppercase tracking-wide text-[var(--muted)] w-[140px]">
                      Токены
                    </th>
                    <th scope="col" className="px-3.5 py-2.5 text-left text-[11px] font-[600] uppercase tracking-wide text-[var(--muted)] w-[170px]">
                      Последняя операция
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {overview.items.map((row) => (
                    <tr
                      key={row.accountId}
                      className="border-b border-[var(--border)] last:border-b-0 transition-colors hover:bg-[var(--accent-weak)]"
                    >
                      <td className="px-3.5 py-2.5 text-[13px]">
                        <Link
                          to={`/users/show/${row.accountId}`}
                          className="text-[var(--accent)] font-[500] hover:underline"
                        >
                          {row.email ?? row.accountId}
                        </Link>
                        {row.email && (
                          <div className="font-mono text-[11px] text-[var(--muted)] mt-0.5">
                            {row.accountId}
                          </div>
                        )}
                      </td>
                      <td className="px-3.5 py-2.5 text-right text-[13px] tabular-nums font-[600]">
                        {formatCost(row.totalCost)}
                      </td>
                      <td className="px-3.5 py-2.5 text-right text-[13px] tabular-nums">
                        {formatCount(row.operations)}
                      </td>
                      <td className="px-3.5 py-2.5 text-right text-[13px] tabular-nums text-[var(--muted)]">
                        {formatCount(row.totalTokens)}
                      </td>
                      <td className="px-3.5 py-2.5 text-[12px] font-mono text-[var(--muted)]">
                        {formatDateTime(row.lastActivityAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="flex items-center justify-between px-5 py-3 border-t border-[var(--border)]">
              <span className="text-[12px] text-[var(--muted)]">
                Страница {page + 1}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(p - 1, 0))}
                  className="h-9 px-3 rounded-[var(--radius-sm)] text-[13px] font-[500] border border-[var(--border)] text-[var(--fg)] bg-[var(--surface-2)] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Назад
                </button>
                <button
                  type="button"
                  disabled={overview.items.length < PAGE_SIZE}
                  onClick={() => setPage((p) => p + 1)}
                  className="h-9 px-3 rounded-[var(--radius-sm)] text-[13px] font-[500] border border-[var(--border)] text-[var(--fg)] bg-[var(--surface-2)] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Вперёд
                </button>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
